import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, PartyPopper, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const attempts = useRef(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current >= 10) {
          setState("timeout");
          return;
        }
      } catch {
        if (attempts.current >= 10) { setState("timeout"); return; }
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [cancelled, sessionId, refresh]);

  const card = "bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-8 text-center space-y-4";

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        {state === "checking" && (
          <div className={card} data-testid="payment-checking">
            <Loader2 size={40} className="animate-spin mx-auto" />
            <p className="font-heading text-xl font-semibold">Confirming your payment...</p>
            <p className="text-sm text-gray-600">Hang tight, this takes a few seconds.</p>
          </div>
        )}
        {state === "paid" && (
          <div className={card} data-testid="payment-success">
            <PartyPopper size={40} className="mx-auto text-[#51CF66]" />
            <p className="font-heading text-2xl font-semibold">You're all set!</p>
            <p className="text-sm text-gray-600">Subscription active — Mia's ready to coach.</p>
            <Link to="/" data-testid="payment-success-home"
              className="inline-block bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] font-bold px-6 py-3">
              Go to StudySpark
            </Link>
          </div>
        )}
        {state === "cancelled" && (
          <div className={card} data-testid="payment-cancelled">
            <XCircle size={40} className="mx-auto text-[#FF6B6B]" />
            <p className="font-heading text-2xl font-semibold">Checkout cancelled</p>
            <p className="text-sm text-gray-600">No worries — you can subscribe whenever you're ready.</p>
            <Link to="/billing" data-testid="payment-cancel-back"
              className="inline-block bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] font-bold px-6 py-3">
              Back to plans
            </Link>
          </div>
        )}
        {state === "timeout" && (
          <div className={card} data-testid="payment-timeout">
            <XCircle size={40} className="mx-auto text-[#FF6B6B]" />
            <p className="font-heading text-2xl font-semibold">We couldn't confirm the payment</p>
            <p className="text-sm text-gray-600">If you were charged, your subscription will activate shortly — check back in a minute.</p>
            <Link to="/billing" className="inline-block bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] font-bold px-6 py-3">
              Back to plans
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
